import { useQuery } from '@tanstack/react-query';
import ApiConfig from 'src/config/api-config';
import { auth } from 'src/config/firebase';
import { sessionStore } from 'src/store/sessionStore';
import { ChatMessage } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/Messages/types';

export interface SearchMatchItem {
  id: string;
  body: string;
  send_time: string;
  sender_uid: string;
  sender_name: string;
  message_type: string;
  attachments?: any[];
}

export interface SearchChatMessagesResponse {
  chatId: string;
  query: string;
  totalMatches: number;
  matches: SearchMatchItem[];
}

export interface ContextWindowResponse {
  chatId: string;
  targetMessageId: string;
  messages: ChatMessage[];
  hasMoreEarlier: boolean;
  hasMoreLater: boolean;
}

export interface SearchChatMessagesOptions {
  limit?: number;
  inquiryId?: string;
  excludeInquiries?: boolean;
}

export const searchConversationMessages = async (
  chatId: string,
  query: string,
  options?: SearchChatMessagesOptions,
): Promise<SearchChatMessagesResponse> => {
  if (!chatId || !query.trim()) {
    return {
      chatId,
      query,
      totalMatches: 0,
      matches: [],
    };
  }

  const token = (await auth.currentUser?.getIdToken()) || sessionStore.getState().token;
  if (!token) {
    throw new Error('Authentication required.');
  }

  const queryParams = new URLSearchParams({
    chatId,
    query: query.trim(),
    limit: String(options?.limit ?? 100),
  });

  if (options?.inquiryId) {
    queryParams.set('inquiryId', options.inquiryId);
  }
  if (options?.excludeInquiries) {
    queryParams.set('excludeInquiries', 'true');
  }

  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_SEARCH_CONVERSATION_MESSAGES}?${queryParams.toString()}`;
  const response = await fetch(apiUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.error?.message || 'Failed to search messages.');
  }

  return response.json();
};

export const useSearchChatMessages = (
  chatId: string,
  query: string,
  enabled: boolean = true,
  options?: SearchChatMessagesOptions,
) => {
  const trimmed = query.trim();
  const inquiryId = options?.inquiryId || '';
  const excludeInquiries = Boolean(options?.excludeInquiries);

  return useQuery({
    queryKey: ['search-chat-messages', chatId, trimmed, inquiryId, excludeInquiries],
    queryFn: () => searchConversationMessages(chatId, trimmed, options),
    enabled: Boolean(chatId) && trimmed.length > 0 && enabled,
    staleTime: 10 * 1000,
  });
};

export const fetchMessageContextWindow = async (
  chatId: string,
  targetMessageId: string,
  surroundingLimit: number = 25,
): Promise<ContextWindowResponse> => {
  if (!chatId || !targetMessageId) {
    throw new Error('chatId and targetMessageId are required.');
  }

  const token = (await auth.currentUser?.getIdToken()) || sessionStore.getState().token;
  if (!token) {
    throw new Error('Authentication required.');
  }

  const queryParams = new URLSearchParams({
    chatId,
    targetMessageId,
    surroundingLimit: String(surroundingLimit),
  });

  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_GET_MESSAGE_CONTEXT_WINDOW}?${queryParams.toString()}`;
  const response = await fetch(apiUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.error?.message || 'Failed to load message context.');
  }

  return response.json();
};
